import React, { Suspense, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import AdminLayout from 'layouts/admin';
import CompanyLayout from 'layouts/empresa';
import AuthLayout from 'layouts/auth';
import AuthWrapper from 'components/auth/AuthWrapper';
import CookieBanner from 'components/cookies/CookieBanner';
import RouteLoadingFallback from 'components/navigation/RouteLoadingFallback';
import PaymentComplete from 'views/admin/main/payment/PaymentComplete';
import AddonPaymentComplete from 'views/admin/main/payment/AddonPaymentComplete';
import 'assets/css/Plugins.css';
import {
  TermsPage,
  PrivacyPage,
  CookiesPage,
  RefundPage,
  AUPPage,
} from 'views/legal';
import { BlogPage, BlogPostPage } from 'views/blog';
import CredentialViewPage from 'views/credentials/CredentialViewPage';
import FormationClaimPage from 'views/formation/FormationClaimPage';
import ProposalAcceptancePage from 'views/public/ProposalAcceptancePage';
import { ROUTES } from 'constants/routes';
import config from 'config/environment';
import { NotificationProvider } from 'contexts/NotificationContext';
import { ToastProvider } from 'components/ui/NotificationToast';

const VisualDirectionSamples = React.lazy(() =>
  import('views/admin/main/design/VisualDirectionSamples')
);
const EnterpriseAuthorizationReviews = React.lazy(() =>
  import('views/enterprise/EnterpriseAuthorizationReviews')
);
const EnterpriseLayout = React.lazy(() =>
  import('views/enterprise/EnterpriseLayout')
);

const PaymentElementsRoute = ({ children }) => {
  const [stripePromise] = useState(() =>
    config.stripe?.publicKey ? loadStripe(config.stripe.publicKey) : null
  );

  if (!stripePromise) {
    return children;
  }

  return <Elements stripe={stripePromise}>{children}</Elements>;
};

const App = () => {
  return (
    <ToastProvider>
      <Routes>
        {/* Public routes - accessible without authentication (legal pages) */}
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="cookies" element={<CookiesPage />} />
        <Route path="refund-policy" element={<RefundPage />} />
        <Route path="acceptable-use" element={<AUPPage />} />
        {/* Blog routes - public, no auth required */}
        <Route path="blog" element={<BlogPage />} />
        <Route path="blog/:slug" element={<BlogPostPage />} />
        {/* Credential view page - public, token-based auth */}
        <Route path="credentials" element={<CredentialViewPage />} />
        <Route
          path="propuestas/aceptar/:token"
          element={<ProposalAcceptancePage />}
        />
        {process.env.NODE_ENV === 'development' ? (
          <Route
            path="design-samples"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <VisualDirectionSamples />
              </Suspense>
            }
          />
        ) : null}
        {/* Legacy routes for backwards compatibility */}
        <Route path="terminos" element={<Navigate to="/terms" replace />} />
        <Route path="privacidad" element={<Navigate to="/privacy" replace />} />

        {/* All other routes wrapped with AuthWrapper */}
        <Route
          path="*"
          element={
            <AuthWrapper>
              <NotificationProvider>
                <Routes>
                  {/* Auth routes (login, signup) - shown when not authenticated */}
                  <Route path="auth/*" element={<AuthLayout />} />
                  <Route
                    path="formation/claim"
                    element={
                      <PaymentElementsRoute>
                        <FormationClaimPage />
                      </PaymentElementsRoute>
                    }
                  />

                  {/* Payment completion page - handles redirect from Stripe crypto checkout */}
                  <Route
                    path="payment-complete"
                    element={
                      <PaymentElementsRoute>
                        <PaymentComplete />
                      </PaymentElementsRoute>
                    }
                  />

                  {/* Addon payment completion page - handles redirect from Stripe crypto checkout for addon purchases */}
                  <Route
                    path="addon-payment-complete"
                    element={
                      <PaymentElementsRoute>
                        <AddonPaymentComplete />
                      </PaymentElementsRoute>
                    }
                  />

                  {/* Protected app routes - only shown when authenticated */}
                  <Route path="empresa/*" element={<CompanyLayout />} />
                  <Route
                    path="enterprise-authorizations"
                    element={
                      <Suspense fallback={<RouteLoadingFallback />}>
                        <EnterpriseAuthorizationReviews />
                      </Suspense>
                    }
                  />
                  <Route
                    path="enterprise/*"
                    element={
                      <Suspense fallback={<RouteLoadingFallback />}>
                        <EnterpriseLayout />
                      </Suspense>
                    }
                  />
                  <Route path="admin/panel" element={<AdminLayout />} />

                  {/* Default redirect:
                  - If not authenticated: AuthWrapper redirects to Auth0
                  - If authenticated: Goes to /empresa/home (redirects to selected company dashboard)
              */}
                  <Route
                    path="/"
                    element={<Navigate to={ROUTES.HOME} replace />}
                  />
                </Routes>
              </NotificationProvider>
            </AuthWrapper>
          }
        />
      </Routes>

      {/* Global Cookie Banner */}
      <CookieBanner />
    </ToastProvider>
  );
};

export default App;
